import { type CustomerQuoteDetail, formatSizeBreakdown, sizeTotalsOf } from '@repo/api-contracts';
import type { ReactElement, ReactNode } from 'react';
import { formatCount } from '@/shared/lib';
import { QuoteDestinationList } from './quote-destination-list';

export interface QuoteRequestSummaryProps {
  readonly detail: CustomerQuoteDetail;
}

interface SummaryRow {
  readonly label: string;
  readonly value: ReactNode;
}

/**
 * 요청 내용(견적 설계 §7-2) — 고객이 요청 때 입력한 값과 고정된 상품·색상·인쇄 방식 사본.
 * 상품 상세의 제작 정보처럼 상자 없이 얇은 선으로 나눈 이름/값 줄이다. 요청일은 머리에 있다.
 */
export const QuoteRequestSummary = ({ detail }: QuoteRequestSummaryProps): ReactElement => {
  const sizeBreakdown = formatSizeBreakdown(sizeTotalsOf(detail.destinations));
  const rows: SummaryRow[] = [
    { label: '색상', value: detail.design.colorName },
    { label: '인쇄 방식', value: detail.design.printMethodName ?? '없음' },
    {
      label: '총 수량',
      value: (
        <span className="tabular-nums">
          {formatCount(detail.quantity)}개
          {sizeBreakdown !== '' ? (
            <span className="ml-2 text-xs text-muted-foreground">{sizeBreakdown}</span>
          ) : null}
        </span>
      ),
    },
    { label: '희망 납기일', value: detail.desiredDate ?? '미지정' },
    ...(detail.destinations.length > 0
      ? [{ label: '배송지', value: <QuoteDestinationList destinations={detail.destinations} /> }]
      : []),
    ...(detail.companyName !== null ? [{ label: '회사·단체명', value: detail.companyName }] : []),
    { label: '담당자명', value: detail.contactName },
    ...(detail.memo !== null
      ? [{ label: '요청 메모', value: <span className="whitespace-pre-wrap">{detail.memo}</span> }]
      : []),
  ];

  return (
    <section aria-label="요청 내용" className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <p
          aria-hidden
          className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase"
        >
          Request
        </p>
        <h2 className="text-[clamp(1.5rem,2.4vw,2rem)] leading-[1.2] font-bold tracking-[-0.02em] text-balance">
          {detail.productName}
        </h2>
      </div>
      <dl className="divide-y divide-foreground/10 border-t border-foreground/10 text-sm wrap-anywhere">
        {rows.map((row) => (
          <div key={row.label} className="flex gap-4 py-3">
            <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">{row.label}</dt>
            <dd className="min-w-0 flex-1">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};
